import { useCurrentDate, Card, CardImage, H5, H6 } from "@sellar-io/components";
import { SupplierRecentlyAddedNewListResponseType } from "@sellar-io/api-v2/types";
import { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Icon, ProfilePicture } from "../../../../components";

import dayjs from "dayjs";
import { getDropStatus } from "@sellar-io/common";

const SupplierAvailabilityPill = ({
  availableStartDate,
  availableEndDate,
}: {
  availableStartDate: Date;
  availableEndDate: Date;
}) => {
  const currentDate = useCurrentDate();
  const dropStatus = getDropStatus(currentDate, {
    startDate: availableStartDate,
    endDate: availableEndDate,
  });

  if (dropStatus === "ended") {
    return null;
  }

  return (
    <div className="flex w-fit items-center gap-2 rounded-[100px] border border-zinc-100 bg-zinc-100 p-1 text-sm font-medium text-zinc-800">
      {dropStatus === "prelaunch" && (
        <>
          <Icon icon={"CIRCLE"} color={"orange"} size={0.7} />
          <div>Releases {dayjs(availableStartDate).format("DD/MM/YY")}</div>
        </>
      )}
      {dropStatus === "live" && (
        <>
          <Icon icon={"CIRCLE"} color={"green"} size={0.7} />
          <div>
            Active until {dayjs(availableEndDate).format("DD/MM/YY h:mma")}
          </div>
        </>
      )}
    </div>
  );
};

export const DiscoverySupplierCardCompact = ({
  supplier,
  footer,
  onClick,
  className,
  footerClassName,
  description,
  onClickRemove,
}: {
  supplier: SupplierRecentlyAddedNewListResponseType[number] & {
    StorefrontAvailability: {
      type: "continuous" | "scheduled";
      startDate: Date | null;
      endDate: Date | null;
    } | null;
  };
  footer?: ReactNode;
  description?: ReactNode;
  onClick?: () => void;
  onClickRemove?: () => void;
  className?: string;
  footerClassName?: string;
}) => {
  const availableStartDate = supplier.StorefrontAvailability?.startDate;
  const availableEndDate = supplier.StorefrontAvailability?.endDate;
  return (
    <Card
      key={supplier.id}
      className={twMerge(
        "bg-white",
        "relative h-full cursor-pointer snap-center",
        className,
      )}
    >
      <div className="relative pt-[calc(3/5*100%)]">
        <span onClick={onClick}>
          <CardImage
            imageUrl={supplier.imageUrl ?? ""}
            fallback="supplier-hero"
            className="absolute inset-0 h-full w-full"
          />
        </span>
        {onClickRemove && (
          <div
            onClick={onClickRemove}
            className="absolute top-[8px] right-[10px] inline-flex h-[32px] w-[32px] items-center justify-start gap-2.5 p-2 text-white"
          >
            <Icon icon={"CLOSE"} size={1} />
          </div>
        )}
        {availableStartDate && availableEndDate && (
          <div className="absolute top-2 right-2">
            <SupplierAvailabilityPill
              availableStartDate={availableStartDate}
              availableEndDate={availableEndDate}
            />
          </div>
        )}
      </div>
      <div className={"flex flex-col gap-4 p-4"} onClick={onClick}>
        <div className="flex items-center gap-2">
          <ProfilePicture
            size={"medium"}
            logoUrl={supplier.Business.logoUrl}
            name={supplier.Business.name}
            className="rounded-full border-2 border-white bg-white"
            fallback="supplier-badge"
          />
          <div className={"flex flex-col overflow-hidden"}>
            <H5 className="overflow-hidden text-sm font-semibold text-ellipsis whitespace-nowrap text-zinc-800">
              {supplier.Business.name}
            </H5>
            {description ? (
              description
            ) : (
              <H6
                className={
                  "overflow-hidden text-xs text-ellipsis whitespace-nowrap text-zinc-500"
                }
              >
                {supplier.Business.DeliveryAddress.city}
              </H6>
            )}
          </div>
        </div>
      </div>
      {!!footer && (
        <div
          className={twMerge(
            "bottom-0 flex items-center gap-2 rounded-b-lg border-t border-zinc-300 bg-zinc-50 px-2 py-[6px] text-sm font-medium text-zinc-500",
            footerClassName,
          )}
        >
          {footer}
        </div>
      )}
    </Card>
  );
};
